// hooks/use-credits.ts
// SWR hooks for Credits control center

import { useCallback, useMemo, useRef } from 'react'

import useSWR from './use-swr'
import type {
  CreditsBalanceResponse,
  CreditsLedgerFilters,
  CreditsLedgerResponse,
  CreditsLedgerType,
  CreditsSummary,
  CreditsSummaryResponse,
  CreditTransactionsFilters,
  CreditTransactionsResponse,
} from '@/types/credits'

interface UseCreditsLedgerParams {
  cursor: string | null
  limit: number
  filters: CreditsLedgerFilters
}

// New Credits Balance hook
export function useCreditsBalance() {
  const abortRef = useRef<AbortController | null>(null)

  const fetcher = useCallback(async (): Promise<CreditsBalanceResponse> => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    const response = await fetch('/api/control-center/credits/balance', {
      signal: abortRef.current.signal,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to load credits balance')
    }

    return response.json()
  }, [])

  return useSWR<CreditsBalanceResponse>('credits-balance', fetcher, {
    revalidateOnFocus: true,
  })
}

// New Credits Ledger hook
export function useCreditsLedger({ cursor, limit, filters }: UseCreditsLedgerParams) {
  const abortRef = useRef<AbortController | null>(null)

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    if (cursor) {
      params.set('cursor', cursor)
    }
    if (filters.dateRange) {
      params.set('date_range', filters.dateRange)
    }
    if (filters.type && filters.type !== 'all') {
      params.set('type', filters.type)
    }
    if (filters.search) {
      params.set('search', filters.search)
    }
    return params.toString()
  }, [cursor, filters.dateRange, filters.search, filters.type, limit])

  const fetcher = useCallback(async (): Promise<CreditsLedgerResponse> => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    const response = await fetch(`/api/control-center/credits/ledger?${queryString}`,
      { signal: abortRef.current.signal }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to load credits ledger')
    }

    return response.json()
  }, [queryString])

  const key = useMemo(
    () => ['credits-ledger', queryString],
    [queryString]
  )

  return useSWR<CreditsLedgerResponse>(key, fetcher, {
    revalidateOnFocus: false,
  })
}

// Credit Summary hook
export function useCreditSummary(organizationId?: string) {
  const abortRef = useRef<AbortController | null>(null)

  const fetcher = useCallback(async (): Promise<CreditsSummary> => {
    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    const response = await fetch('/api/control/credits/summary', {
      signal: abortRef.current.signal,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to load credits summary')
    }

    const json: CreditsSummaryResponse = await response.json()
    return json.summary
  }, [])

  return useSWR<CreditsSummary>(
    organizationId ? ['credits-summary', organizationId] : null,
    fetcher,
    { revalidateOnFocus: true }
  )
}

// Credit Transactions hook with cursor pagination
export function useCreditTransactions(
  organizationId: string | undefined,
  filters: CreditTransactionsFilters,
  cursor: string | null = null
) {
  const abortRef = useRef<AbortController | null>(null)
  // Stable key based on filters and cursor
  const keyRef = useRef<string | null>(null)

  const fetcher = useCallback(async (): Promise<CreditTransactionsResponse> => {
    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    const params = new URLSearchParams()
    if (filters.dateRange !== 'all') {
      params.set('date_range', filters.dateRange)
    }
    if (filters.type !== 'all') {
      params.set('type', filters.type)
    }
    if (filters.action !== 'all') {
      params.set('action', filters.action)
    }
    if (cursor) {
      params.set('cursor', cursor)
    }
    params.set('limit', '25')

    const url = `/api/control/credits/transactions?${params.toString()}`

    const response = await fetch(url, {
      signal: abortRef.current.signal,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to load credit transactions')
    }

    return response.json()
  }, [filters.dateRange, filters.type, filters.action, cursor])

  // Create stable key
  const newKey = organizationId
    ? `credits-transactions-${organizationId}-${filters.dateRange}-${filters.type}-${filters.action}-${cursor || 'start'}`
    : null

  // Prevent infinite loops by checking key stability
  if (newKey !== keyRef.current) {
    keyRef.current = newKey
  }

  return useSWR<CreditTransactionsResponse>(
    keyRef.current,
    fetcher,
    { revalidateOnFocus: false }
  )
}

// Get unique actions from transactions for filter dropdown
export function useTransactionActions(organizationId?: string) {
  const abortRef = useRef<AbortController | null>(null)

  const fetcher = useCallback(async (): Promise<string[]> => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    const response = await fetch('/api/control/credits/transactions?limit=100', {
      signal: abortRef.current.signal,
    })

    if (!response.ok) {
      return []
    }

    const json: CreditTransactionsResponse = await response.json()
    const actions = new Set<string>()
    json.transactions.forEach((tx) => {
      if (tx.action) {
        actions.add(tx.action)
      }
    })
    return Array.from(actions).sort()
  }, [])

  return useSWR<string[]>(
    organizationId ? ['credits-actions', organizationId] : null,
    fetcher,
    { revalidateOnFocus: false }
  )
}

export function isLedgerType(value: string | null | undefined): value is CreditsLedgerType {
  return value === 'reserve' || value === 'confirm' || value === 'refund' || value === 'spend'
}
